import { useState, type FormEvent } from 'react';
import { emailSchema, type ParentConnection, type TrustedConnection } from '@teentack/shared';
import {
  useDecideParentConnection,
  useParentConnections,
  useRemoveTrusted,
  useRequestParentConnection,
  useSetTrusted,
  useTrustedConnections,
} from '@/features/connections/use-connections';
import { ParentShell } from '@/features/parent/ParentShell';
import { EmptyState } from '@/components/ui/Feedback';
import { Tabs } from '@/components/ui/Tabs';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorNote, InfoNote } from '@/components/Form';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { BadgeCheckIcon, MapPinIcon, ShieldCheckIcon, UsersIcon } from '@/components/ui/Icons';
import { presentApiError } from '@/lib/form-errors';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return `${parts[0]?.charAt(0) ?? ''}${parts.length > 1 ? (parts.at(-1)?.charAt(0) ?? '') : ''}`.toUpperCase();
}

/**
 * Where a guardian builds the trust graph. [Parent steps 25-27]
 *
 * Two halves, because they answer different questions: which employers do I
 * vouch for, and which guardians do I know. Together they decide what every
 * teen linked to this parent is told about a job before they apply.
 */
export function ParentNetworkPage() {
  const trusted = useTrustedConnections({ pageSize: 100 });
  const [employerKind, setEmployerKind] = useState<'ALL' | 'INDIVIDUAL' | 'ORGANIZATION'>('ALL');
  const connections = useParentConnections({ pageSize: 100 });

  const setTrusted = useSetTrusted();
  const removeTrusted = useRemoveTrusted();
  const requestConnection = useRequestParentConnection();
  const decide = useDecideParentConnection();

  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (caught) {
      setError(presentApiError(caught, 'Something went wrong').message);
    }
  }

  function invite(event: FormEvent): void {
    event.preventDefault();
    if (!emailSchema.safeParse(email).success) {
      setError('Enter a valid email address');
      return;
    }
    void run(async () => {
      await requestConnection.mutateAsync({ email: email.trim() });
      setEmail('');
    }, 'Request sent. They will see it on their console.');
  }

  const trustedList = trusted.data?.items ?? [];
  const connected = trustedList.filter((entry) => entry.status === 'CONNECTED');
  const blocked = trustedList.filter((entry) => entry.status === 'BLOCKED');

  /*
   * The reference splits these into two steps because a guardian weighs them
   * differently: a neighbour who needs a lawn mowed is not the same decision as
   * a shop that hires. Filtered here rather than server-side — the whole list
   * is already loaded, and re-fetching to hide half of it would be slower and
   * would lose the counts. [Parent steps 25-26]
   */
  const organizations = connected.filter((entry) => entry.jobPoster.organizationName !== null);
  const individuals = connected.filter((entry) => entry.jobPoster.organizationName === null);
  const employers =
    employerKind === 'ORGANIZATION'
      ? organizations
      : employerKind === 'INDIVIDUAL'
        ? individuals
        : connected;

  const all = connections.data?.items ?? [];
  const incoming = all.filter((c) => c.incoming && c.status === 'PENDING');
  const accepted = all.filter((c) => c.status === 'ACCEPTED');
  const outgoing = all.filter((c) => !c.incoming && c.status === 'PENDING');

  const busy = setTrusted.isPending || removeTrusted.isPending || decide.isPending;

  return (
    <ParentShell
      title="Trusted network"
      description="Employers you vouch for and guardians you know. Your teen sees this on every job — and so does every guardian connected to you."
    >
      <div className="mb-6 space-y-3">
        <ErrorNote message={error} />
        {notice && <InfoNote>{notice}</InfoNote>}
      </div>

      <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
        {/* --- Employers ---------------------------------------------------- */}
        <section>
          <div className="mb-4 flex items-baseline justify-between gap-3">
            <h2 className="text-base font-bold text-ink">Employers you trust</h2>
            <span className="text-xs font-semibold text-slate-400">
              {connected.length} vouched
            </span>
          </div>

          {/* Only worth offering once there is a mix to sort through. */}
          {organizations.length > 0 && individuals.length > 0 && (
            <div className="mb-4">
              <Tabs
                label="Kind of employer"
                value={employerKind}
                onChange={setEmployerKind}
                tabs={[
                  { value: 'ALL', label: 'All', count: connected.length },
                  { value: 'INDIVIDUAL', label: 'People', count: individuals.length },
                  { value: 'ORGANIZATION', label: 'Organizations', count: organizations.length },
                ]}
              />
            </div>
          )}

          {trusted.isPending && <Skeleton className="h-32" />}

          {employers.length === 0 && !trusted.isPending && (
            <EmptyState icon={<BadgeCheckIcon className="size-6" />} title="No employers yet">
              Open any job and mark the employer trusted. Your teen then sees “trusted by your
              guardian” on everything they post.
            </EmptyState>
          )}

          <ul className="space-y-3">
            {employers.map((entry) => (
              <EmployerRow
                key={entry.id}
                entry={entry}
                busy={busy}
                onBlock={() =>
                  void run(
                    () =>
                      setTrusted.mutateAsync({
                        jobPosterId: entry.jobPoster.id,
                        status: 'BLOCKED',
                      }),
                    `${entry.jobPoster.name} blocked.`,
                  )
                }
                onRemove={() =>
                  void run(
                    () => removeTrusted.mutateAsync(entry.jobPoster.id),
                    `${entry.jobPoster.name} removed from your list.`,
                  )
                }
              />
            ))}
          </ul>

          {blocked.length > 0 && (
            <>
              <h3 className="mt-8 mb-3 text-sm font-bold text-slate-500">Blocked</h3>
              <ul className="space-y-3">
                {blocked.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-100 bg-red-50/50 p-4"
                  >
                    <div>
                      <p className="text-sm font-bold text-ink">{entry.jobPoster.name}</p>
                      <p className="mt-0.5 text-xs text-red-700">
                        Your teens are warned about this employer
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () => removeTrusted.mutateAsync(entry.jobPoster.id),
                          `${entry.jobPoster.name} unblocked.`,
                        )
                      }
                    >
                      Unblock
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        {/* --- Guardians ---------------------------------------------------- */}
        <section>
          <div className="mb-4 flex items-baseline justify-between gap-3">
            <h2 className="text-base font-bold text-ink">Guardians you know</h2>
            <span className="text-xs font-semibold text-slate-400">
              {accepted.length} connected
            </span>
          </div>

          <form
            onSubmit={invite}
            className="mb-5 rounded-2xl border border-line bg-white p-5 shadow-soft"
          >
            <Input
              label="Connect with another guardian"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="their@email.com"
              hint="They decide whether to accept. Neither of you sees the other's children."
            />
            <Button
              type="submit"
              className="mt-3 w-full"
              disabled={requestConnection.isPending || !email.trim()}
            >
              {requestConnection.isPending ? 'Sending…' : 'Send request'}
            </Button>
          </form>

          {incoming.length > 0 && (
            <>
              <h3 className="mb-3 text-sm font-bold text-sun-700">Waiting on you</h3>
              <ul className="mb-6 space-y-3">
                {incoming.map((connection) => (
                  <li
                    key={connection.id}
                    className="rounded-2xl border border-sun-200 bg-sun-50 p-4"
                  >
                    <ParentIdentity connection={connection} />
                    <div className="mt-3 flex gap-2">
                      <Button
                        variant="success"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () =>
                              decide.mutateAsync({
                                connectionId: connection.id,
                                decision: 'ACCEPT',
                              }),
                            `Connected with ${connection.parent.name}.`,
                          )
                        }
                      >
                        Accept
                      </Button>
                      <Button
                        variant="outline"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () =>
                              decide.mutateAsync({
                                connectionId: connection.id,
                                decision: 'DECLINE',
                              }),
                            'Request declined.',
                          )
                        }
                      >
                        Decline
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {accepted.length === 0 && incoming.length === 0 && !connections.isPending && (
            <EmptyState icon={<UsersIcon className="size-6" />} title="No guardians yet">
              Connect with parents you already know. When one of them trusts an employer, your teen
              sees it — which makes your next approval an easy one.
            </EmptyState>
          )}

          <ul className="space-y-3">
            {accepted.map((connection) => (
              <li
                key={connection.id}
                className="rounded-2xl border border-line bg-white p-4 shadow-soft"
              >
                <ParentIdentity connection={connection} />
              </li>
            ))}
          </ul>

          {outgoing.length > 0 && (
            <>
              <h3 className="mt-8 mb-3 text-sm font-bold text-slate-500">Sent, awaiting reply</h3>
              <ul className="space-y-3">
                {outgoing.map((connection) => (
                  <li
                    key={connection.id}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-line bg-canvas p-4"
                  >
                    <p className="text-sm font-semibold text-slate-600">{connection.parent.name}</p>
                    <Badge tone="neutral">Pending</Badge>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
    </ParentShell>
  );
}

function ParentIdentity({ connection }: { connection: ParentConnection }) {
  return (
    <div className="flex items-center gap-3">
      {connection.parent.photoUrl ? (
        <img src={connection.parent.photoUrl} alt="" className="size-10 rounded-xl object-cover" />
      ) : (
        <span
          aria-hidden="true"
          className="grid size-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-xs font-extrabold text-slate-500"
        >
          {initials(connection.parent.name)}
        </span>
      )}
      <div className="min-w-0">
        <p className="text-sm font-bold text-ink">{connection.parent.name}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
          {connection.parent.city && (
            <span className="inline-flex items-center gap-1">
              <MapPinIcon className="size-3.5 text-slate-400" />
              {[connection.parent.city, connection.parent.state].filter(Boolean).join(', ')}
            </span>
          )}
          {connection.mutualTrustedCount > 0 && (
            <span className="font-semibold text-brand-600">
              {connection.mutualTrustedCount} employer
              {connection.mutualTrustedCount === 1 ? '' : 's'} you both trust
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

function EmployerRow({
  entry,
  busy,
  onBlock,
  onRemove,
}: {
  entry: TrustedConnection;
  busy: boolean;
  onBlock: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="rounded-2xl border border-line bg-white p-4 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-bold text-ink">
            {/* An organization leads with its own name: that is what a guardian
                recognises, and the person is the contact behind it.
                [Parent step 26] */}
            {entry.jobPoster.organizationName ?? entry.jobPoster.name}
            <ShieldCheckIcon className="size-4 text-safe-600" />
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
            {entry.jobPoster.organizationName && <span>{entry.jobPoster.name}</span>}
            {entry.jobPoster.professionalTitle && <span>{entry.jobPoster.professionalTitle}</span>}
            {entry.jobPoster.location && (
              <span className="inline-flex items-center gap-1">
                <MapPinIcon className="size-3.5 text-slate-400" />
                {entry.jobPoster.location}
              </span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Badge tone={entry.jobPoster.organizationName ? 'brand' : 'neutral'}>
            {entry.jobPoster.organizationName ? 'Organization' : 'Individual'}
          </Badge>
          {entry.jobsCompleted > 0 && (
            <Badge tone="safe">
              {entry.jobsCompleted} job{entry.jobsCompleted === 1 ? '' : 's'} done
            </Badge>
          )}
        </div>
      </div>

      <div className="mt-3 flex gap-2 border-t border-line pt-3">
        <Button variant="ghost" disabled={busy} onClick={onRemove}>
          Remove
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onBlock}>
          Block
        </Button>
      </div>
    </li>
  );
}
